import { addMoney } from '@shared/lib/money';
import { createSelector } from 'reselect';

import { CheckoutState } from '../reducers';
import { SubsidiesInfoByPassengers } from './reducers';
import { Money } from '../../../types';
import { getAirticketPriceToPay } from '../orderPrice/selectors';

export const getCheckoutState = (state: CheckoutState) => state;

export const getSubsidiesState = (state: CheckoutState) => state.subsidies;

export const getSubsidiesByPassengers = createSelector(
	getSubsidiesState,
	state => state.availableSubsidiesByPassengers
);

export const getSelectedSubsidies = createSelector(getSubsidiesState, state => state.selectedSubsidies);

export const getIsSubsidiesLoading = createSelector(getSubsidiesState, state => state.isLoading);
export const getIsSubsidiesModalOpen = createSelector(getSubsidiesState, state => state.resultsOpen);

export const getInitialPrice = createSelector(getAirticketPriceToPay, airticketsPrice => airticketsPrice);

export const getSubsidiesResults = createSelector(
	getSubsidiesState,
	state => state.subsidiesResultByPassengersSegments
);

export const getHasAvailableSubsidies = createSelector(getSubsidiesState, subsidiesState => {
	return Object.values(subsidiesState.availableSubsidiesByPassengers).some(availableSubsidy => !!availableSubsidy);
});

export const getAvailableSubsidiesTypesByPassengers = createSelector(
	getSubsidiesByPassengers,
	subsidiesByPassengers => {
		const result: { [passengerId: string]: SubsidiesInfoByPassengers[] } = {};

		for (const [passengerId, passengerSubsidies] of Object.entries(subsidiesByPassengers)) {
			result[passengerId] = [];

			for (const availableSubsidy of passengerSubsidies) {
				if (!result[passengerId].find(subsidy => subsidy.type === availableSubsidy.type)) {
					result[passengerId].push(availableSubsidy);
				}
			}
		}

		return result;
	}
);

export const getSelectedSubsidiesPricesByPassengers = createSelector(
	getSubsidiesState,
	getSelectedSubsidies,
	getInitialPrice,
	(subsidiesState, selectedSubsidies, initialPrice) => {
		const resultPriceByPassengers: { [passengerId: string]: Money } = {};

		for (const [passengerId, passengerSubsidies] of Object.entries(selectedSubsidies)) {
			let resultPrice: Money = {
				currency: initialPrice?.currency,
				amount: 0
			};

			if (subsidiesState.subsidiesResultByPassengersSegments[passengerId]) {
				for (const [, segmentSubsidies] of Object.entries(
					subsidiesState.subsidiesResultByPassengersSegments[passengerId]
				)) {
					for (const subsidy of segmentSubsidies) {
						resultPrice = addMoney(resultPrice, subsidy.initialPrice);
					}
				}
			}

			for (const selectedSubsidy of passengerSubsidies) {
				const passengerAvailableSubsidies = subsidiesState.subsidiesResultByPassengersSegments[passengerId];
				for (const [subsidySegmentId, availableSubsidy] of Object.entries(passengerAvailableSubsidies)) {
					if (selectedSubsidy.segmentId === subsidySegmentId) {
						const relatedAvailableSubsidy = availableSubsidy.find(
							subsidy => selectedSubsidy.subsidyId === subsidy.id
						);

						if (relatedAvailableSubsidy) {
							resultPrice.amount -= relatedAvailableSubsidy.discount.amount;
						}
					}
				}
			}

			resultPriceByPassengers[passengerId] = resultPrice;
		}

		return resultPriceByPassengers;
	}
);

export const getSelectedSubsidiesPricesByPassengersFactory = (
	priceType: 'resultPrice' | 'initialPrice' | 'discount'
) => {
	return createSelector(
		getSubsidiesState,
		getSelectedSubsidies,
		getInitialPrice,
		(subsidiesState, selectedSubsidies, initialPrice) => {
			const resultPriceByPassengers: { [passengerId: string]: Money } = {};

			for (const [passengerId, passengerSubsidies] of Object.entries(selectedSubsidies)) {
				let resultPrice: Money = {
					currency: initialPrice?.currency,
					amount: 0
				};

				for (const selectedSubsidy of passengerSubsidies) {
					const passengerAvailableSubsidies = subsidiesState.subsidiesResultByPassengersSegments[passengerId];
					for (const [subsidySegmentId, availableSubsidy] of Object.entries(passengerAvailableSubsidies)) {
						if (selectedSubsidy.segmentId === subsidySegmentId) {
							const relatedAvailableSubsidy = availableSubsidy.find(
								subsidy => selectedSubsidy.subsidyId === subsidy.id
							);

							if (relatedAvailableSubsidy) {
								resultPrice = addMoney(resultPrice, relatedAvailableSubsidy[priceType]);
							}
						}
					}
				}

				resultPriceByPassengers[passengerId] = resultPrice;
			}

			return resultPriceByPassengers;
		}
	);
};

export const getHasSelectedSubsidies = createSelector(getSelectedSubsidies, selectedSubsidies => {
	return Object.values(selectedSubsidies).some(subsidies => subsidies.length > 0);
});

export const getSubsidiesError = createSelector(getSubsidiesState, state => state.selectSubsidiesError);

export const getCheckedTravellers = createSelector(getSubsidiesState, state => state.checkedTravellers);

export const getTotalSelectedSubsidiesPrices = createSelector(
	getInitialPrice,
	getSelectedSubsidiesPricesByPassengersFactory('discount'),
	(initialPrice, discountByPassengers) => {
		const totalPrice: Money = {
			...initialPrice
		};

		for (const passenger of Object.values(discountByPassengers)) {
			totalPrice.amount -= passenger.amount;
			totalPrice.currency = passenger.currency;
		}

		return totalPrice;
	}
);
